/**
 * Lightweight DOM overlay helpers — fade-in panels for run summary, town menus,
 * etc. Phaser handles the game world; this just owns the HTML chrome.
 */

export interface OverlayButton {
  label: string;
  primary?: boolean;
  onClick: () => void;
}

export interface OverlayContent {
  title: string;
  subtitle?: string;
  rows: { label: string; value: string | number }[];
  buttons: OverlayButton[];
  flavor?: string;
}

let activeOverlay: HTMLElement | null = null;

export function showOverlay(content: OverlayContent): void {
  hideOverlay();

  const root = document.createElement('div');
  root.className = 'wb-overlay';
  root.innerHTML = `
    <div class="wb-overlay-bg"></div>
    <div class="wb-overlay-panel">
      <h1 class="wb-overlay-title">${escape(content.title)}</h1>
      ${content.subtitle ? `<p class="wb-overlay-subtitle">${escape(content.subtitle)}</p>` : ''}
      ${content.flavor ? `<p class="wb-overlay-flavor">${escape(content.flavor)}</p>` : ''}
      <dl class="wb-overlay-rows">
        ${content.rows.map(r =>
          `<div class="wb-overlay-row"><dt>${escape(r.label)}</dt><dd>${escape(String(r.value))}</dd></div>`
        ).join('')}
      </dl>
      <div class="wb-overlay-buttons"></div>
    </div>
  `;

  const buttonsEl = root.querySelector('.wb-overlay-buttons')!;
  for (const b of content.buttons) {
    const el = document.createElement('button');
    el.className = 'wb-btn' + (b.primary ? ' wb-btn-primary' : '');
    el.textContent = b.label;
    el.onclick = () => { b.onClick(); };
    buttonsEl.appendChild(el);
  }

  document.body.appendChild(root);
  activeOverlay = root;
}

export function hideOverlay(): void {
  if (activeOverlay) {
    activeOverlay.remove();
    activeOverlay = null;
  }
}

export function toast(message: string, durationMs = 1800): void {
  const el = document.createElement('div');
  el.className = 'wb-toast';
  el.textContent = message;
  document.body.appendChild(el);
  setTimeout(() => {
    el.classList.add('wb-toast-fade');
    setTimeout(() => el.remove(), 320);
  }, durationMs);
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]!));
}

// Inject styles once.
const STYLE_ID = 'wb-overlay-styles';
if (!document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .wb-overlay {
      position: fixed; inset: 0; z-index: 100;
      display: flex; align-items: center; justify-content: center;
      animation: wbFade 220ms ease-out;
    }
    .wb-overlay-bg {
      position: absolute; inset: 0;
      background: radial-gradient(circle at center, rgba(0,0,0,0.6), rgba(0,0,0,0.92));
    }
    .wb-overlay-panel {
      position: relative;
      min-width: 360px; max-width: 520px;
      padding: 32px 36px;
      background: #12100c;
      border: 1px solid #3a3327;
      border-radius: 8px;
      color: #f4ecd6;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
      box-shadow: 0 18px 50px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,220,160,0.08);
    }
    .wb-overlay-title {
      margin: 0 0 4px; font-size: 24px; letter-spacing: 0.02em;
      color: #ffd54a;
      font-weight: 600;
    }
    .wb-overlay-subtitle { margin: 0 0 18px; color: #c8bfa3; font-size: 13px; }
    .wb-overlay-flavor { margin: 0 0 18px; color: #9a8f7a; font-size: 12px; font-style: italic; }
    .wb-overlay-rows { margin: 0 0 24px; padding: 0; }
    .wb-overlay-row {
      display: flex; justify-content: space-between;
      padding: 6px 0; border-bottom: 1px dotted #3a3327;
      font-size: 13px;
    }
    .wb-overlay-row dt { color: #9a8f7a; margin: 0; }
    .wb-overlay-row dd { color: #f4ecd6; margin: 0; font-variant-numeric: tabular-nums; }
    .wb-overlay-buttons { display: flex; gap: 10px; justify-content: flex-end; }
    .wb-btn {
      padding: 10px 18px;
      background: #2a2519;
      border: 1px solid #4a4030;
      color: #f4ecd6;
      border-radius: 4px;
      font-family: inherit;
      font-size: 13px;
      cursor: pointer;
      transition: background 140ms, border-color 140ms;
    }
    .wb-btn:hover { background: #3a3220; border-color: #6a5a3a; }
    .wb-btn-primary {
      background: #6e5419; border-color: #ffd54a;
      color: #fff8d6;
    }
    .wb-btn-primary:hover { background: #8a6820; }
    .wb-toast {
      position: fixed; left: 50%; bottom: 64px;
      transform: translateX(-50%);
      padding: 10px 18px;
      background: rgba(18,16,12,0.95); border: 1px solid #3a3327;
      color: #f4ecd6; border-radius: 4px;
      font-family: -apple-system, system-ui, sans-serif; font-size: 13px;
      z-index: 200;
      animation: wbToastIn 240ms ease-out;
    }
    .wb-toast-fade { opacity: 0; transition: opacity 300ms; }
    @keyframes wbFade { from { opacity: 0; } to { opacity: 1; } }
    @keyframes wbToastIn {
      from { transform: translate(-50%, 16px); opacity: 0; }
      to   { transform: translate(-50%, 0); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}
